// useBookingEstimate — fetches per-property nightly rates from
// /api/pricing/{hotel_slug} and turns the booking-widget blocks into
// honest "ballpark" estimates. Rates are stored in EUR at the source,
// per room type, per board basis (room_only / bb / hb / fb).
//
// Estimate logic per block:
//   nights = floor(check_out - check_in)
//   per_night = price for the chosen board basis, picked from a row
//               whose `room_name` matches the typed `room_type` (case-
//               insensitive substring). If no match → average across
//               all rows. If no pricing doc yet → null (no estimate).
//   subtotal  = nights × rooms × per_night
//   currency  = "EUR"

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const BOARD_KEY_MAP = {
  room_only:  "room_only",
  breakfast:  "bb",
  half_board: "hb",
  full_board: "fb",
};

const pickRow = (rows, roomType) => {
  if (!rows || rows.length === 0) return null;
  if (roomType && roomType.trim()) {
    const q = roomType.trim().toLowerCase();
    const match = rows.find((r) => {
      const names = r.room_name || {};
      return ["en", "fr", "es"].some((k) =>
        (names[k] || "").toLowerCase().includes(q) ||
        q.includes((names[k] || "").toLowerCase())
      );
    });
    if (match) return match;
  }
  return null; // signal "no specific match — caller will average"
};

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const computeForBlock = (block, doc) => {
  if (!doc || !Array.isArray(doc.rows) || doc.rows.length === 0) return null;
  if (!block.check_in || !block.check_out) return null;
  const di = new Date(block.check_in);
  const dout = new Date(block.check_out);
  const nights = Math.round((dout - di) / 86400000);
  if (!Number.isFinite(nights) || nights <= 0) return null;
  const rooms = Array.isArray(block.rooms) ? block.rooms : [];
  if (rooms.length === 0) return null;

  const perRoom = rooms.map((room) => {
    const boardKey = BOARD_KEY_MAP[room.board_basis || "breakfast"];
    const specific = pickRow(doc.rows, room.room_type);
    let perNight = null;
    if (specific) {
      perNight = safeNum(specific.prices?.[boardKey]);
    }
    if (!perNight) {
      const prices = doc.rows
        .map((r) => safeNum(r.prices?.[boardKey]))
        .filter(Boolean);
      if (prices.length === 0) return null;
      perNight = prices.reduce((a, b) => a + b, 0) / prices.length;
    }
    const subtotal = nights * perNight;
    return { per_night: perNight, subtotal };
  });

  const validRooms = perRoom.filter(Boolean);
  if (validRooms.length === 0) return null;
  const subtotal = validRooms.reduce((acc, r) => acc + r.subtotal, 0);

  return {
    nights,
    rooms_count: rooms.length,
    perRoom,
    subtotal,
    currency: doc.currency_base || "EUR",
  };
};

const useBookingEstimate = (blocks) => {
  const [docs, setDocs] = useState({}); // slug -> pricing doc (or null on miss)
  // Track in-flight fetches in a ref so re-renders don't re-launch fetches.
  const inflightRef = useRef(new Set());

  // Unique slugs from the current blocks list
  const slugs = useMemo(() => Array.from(new Set(blocks.map((b) => b.slug))), [blocks]);

  useEffect(() => {
    const toFetch = slugs.filter((s) => !(s in docs) && !inflightRef.current.has(s));
    if (toFetch.length === 0) return undefined;

    let cancelled = false;
    toFetch.forEach((slug) => inflightRef.current.add(slug));

    (async () => {
      for (const slug of toFetch) {
        try {
          const r = await fetch(`${API}/api/pricing/${encodeURIComponent(slug)}`, { cache: "no-store" });
          const j = r.ok ? await r.json() : null;
          if (cancelled) return;
          setDocs((cur) => ({ ...cur, [slug]: j }));
        } catch {
          if (!cancelled) setDocs((cur) => ({ ...cur, [slug]: null }));
        } finally {
          inflightRef.current.delete(slug);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [slugs, docs]);

  const estimateForBlock = useCallback(
    (block) => {
      const doc = docs[block.slug];
      if (doc === undefined) return { loading: true };
      if (!doc || !Array.isArray(doc.rows) || doc.rows.length === 0) return null;
      return computeForBlock(block, doc);
    },
    [docs]
  );

  const grandTotal = useMemo(() => {
    let total = 0;
    let anyComplete = false;
    let allLoading = true;
    let missingPricing = false;
    for (const b of blocks) {
      const e = estimateForBlock(b);
      if (e && e.loading) continue;
      allLoading = false;
      if (!e) { missingPricing = true; continue; }
      if (e.subtotal) {
        total += e.subtotal;
        anyComplete = true;
      }
    }
    if (allLoading && blocks.length) return { loading: true };
    return {
      total: anyComplete ? total : null,
      currency: "EUR",
      missingPricing,
    };
  }, [blocks, estimateForBlock]);

  return { estimateForBlock, grandTotal };
};

export default useBookingEstimate;
